import { useQuery, useMutation } from "@apollo/client";
import { useMemo } from "react";
import { GET_TIMELINE_EDITOR } from "MVVM/GraphQL/timelineEditor";
import { MOMENT_CREATE_CLIP } from "MVVM/GraphQL/mutations/momentRequestClip";

export interface TimelineEditorHeader {
  mapName: string;
  tickRate: number;
  totalTicks: number;
}

export interface TimelineEditorPlayer {
  steamId: string;
  name: string;
  observerSlot: number;
}

export interface TimelineEditorKillerData {
  weapon: string;
  headShot: boolean;
  team: string;
}

export interface TimelineEditorKill {
  tick: number;
  killer: string;
  victim: string;
  location: string;
  killerData: TimelineEditorKillerData;
}

export interface TimelineEditorBomb {
  tick: number;
  type: string;
  site: string;
  initiator: string;
}

export interface TimelineEditorRound {
  round: number;
  startTick: number;
  endTick: number;
  winner: string;
  terroristsScore: number;
  counterTerroristsScore: number;
  kills: TimelineEditorKill[];
  bomb: TimelineEditorBomb[];
}

export interface TimelineEditorData {
  header: TimelineEditorHeader;
  players: TimelineEditorPlayer[];
  rounds: TimelineEditorRound[];
}

export interface ReplayCommand {
  tick: number;
  class: string;
  command?: string;
  playerSlot?: number;
  gotoTick?: number;
  steamId64?: string;
}

interface TimelineEditorQueryResult {
  timelineEditor: TimelineEditorData;
}

interface MomentCreateClipResult {
  momentCreateClip: {
    success: boolean;
    errors: Array<{
      field: string;
      code: string;
      message: string;
    }>;
    moments: Array<{
      id: string;
    }>;
  };
}

interface TimelineEditorViewModelProps {
  matchId: string;
}

const TimelineEditorViewModel = ({ matchId }: TimelineEditorViewModelProps) => {
  const activeMatchId = matchId;

  const { data, loading, error } = useQuery<TimelineEditorQueryResult>(
    GET_TIMELINE_EDITOR,
    {
      variables: { matchId: activeMatchId },
      fetchPolicy: "cache-and-network",
      skip: !activeMatchId,
    },
  );

  const [createClip, { loading: createClipLoading, error: createClipError }] =
    useMutation<MomentCreateClipResult>(MOMENT_CREATE_CLIP);

  const processedData = useMemo(() => {
    if (!data?.timelineEditor) return null;

    const uniquePlayers = data.timelineEditor.players.reduce((acc, player) => {
      if (!acc.some((p) => p.steamId === player.steamId)) {
        acc.push(player);
      }
      return acc;
    }, [] as TimelineEditorPlayer[]);

    return {
      ...data.timelineEditor,
      players: uniquePlayers,
    };
  }, [data]);

  const submitReplaySequence = async (
    replaySequence: ReplayCommand[],
    title?: string,
  ) => {
    try {
      const result = await createClip({
        variables: {
          momentCreateClipInput: {
            matchId: activeMatchId,
            replaySequence,
            ...(title && {
              effectsOverrides: {
                title,
              },
            }),
          },
        },
      });

      return {
        success: result.data?.momentCreateClip?.success || false,
        errors: result.data?.momentCreateClip?.errors || [],
        moments: result.data?.momentCreateClip?.moments || [],
      };
    } catch (err) {
      return {
        success: false,
        errors: [{ field: "general", code: "ERROR", message: String(err) }],
        moments: [],
      };
    }
  };

  return {
    data: processedData,
    loading,
    error,
    submitReplaySequence,
    createClipLoading,
    createClipError,
  };
};

export { TimelineEditorViewModel };
